<template>
  <section class="buyer-guidelines reveal animate-up">
    <div class="container">
      <div class="section-header">
        <span class="tag">Process</span>
        <h2>Buyer’s Guidelines</h2>
      </div>

      <div class="guidelines-grid">
        <div
          v-for="(item, index) in guidelines"
          :key="index"
          class="guide-card"
        >
          <div class="card-header">
            <h3>
              <span class="step-num">{{ index + 1 }}.</span> {{ item.title }}
            </h3>
          </div>
          <div class="card-body">
            <ul class="sub-points">
              <li v-for="(point, pIndex) in item.points" :key="pIndex">
                {{ point }}
              </li>
            </ul>
            <p v-if="item.footer" class="guide-footer" v-html="item.footer"></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const guidelines = [
  {
    title: 'Define Your Purpose',
    points: [
      'Investment or self-use?',
      'Short-term resale or long-term holding?',
      'Rental income expectation?',
    ],
    footer: 'Your goal determines location, budget, and property type.',
  },
  {
    title: 'Fix a Realistic Budget',
    points: [
      'Property cost',
      'Stamp duty & registration',

      'Brokerage, Interior & Maintenance',
    ],
    footer:
      'Total Cost of Ownership (TCO) covers everything from purchase and legalities to move-in readiness.',
  },
  {
    title: 'Choose the Right Location',
    points: [
      'Connectivity & infrastructure growth',
      'Schools, hospitals, markets nearby',
      'Future development plans',
      'Rental demand',
    ],
    footer:
      'In Tricity (Chandigarh–Mohali–Panchkula), IT growth drives appreciation.',
  },
  {
    title: 'Verify Legal Documents',
    points: [
      'Clear Title & Approved layout',
      'RERA registration',
      'Occupancy/Completion certificate',
      'No encumbrances',
    ],
    footer: 'Check projects on your state’s RERA portal.',
  },
  {
    title: 'Check Builder’s Reputation',
    points: [
      'Past delivery record',
      'Construction quality',
      'Timely possession',
      'Customer reviews',
    ],
    footer: 'Visit previous completed projects if possible.',
  },
  {
    title: 'Understand Loan Eligibility',
    points: [
      'Check CIBIL score',
      'Compare bank interest rates',
      'Understand EMI affordability',
      'Read loan agreement',
    ],
    footer: 'Avoid overstretching beyond 50% of monthly income.',
  },
  {
    title: 'Inspect Physically',
    points: [
      'Construction quality',
      'Ventilation & sunlight',
      'Water & electricity supply',
      'Common area maintenance',
    ],
    footer: 'Never rely only on brochures.',
  },
  {
    title: 'Evaluate ROI',
    points: [
      'Rental yield %',
      'Appreciation potential',
      'Demand-supply ratio',
      'Upcoming infrastructure',
    ],
    footer: 'Critical for long-term wealth creation.',
  },
  {
    title: 'Review Agreement',
    points: ['Payment schedule', 'Possession date', 'Hidden charges'],
    footer: 'Please read the documents thoroughly before signing.',
  },
  {
    title: 'Think Long-Term',
    points: [
      'Real estate is a long-term asset',
      'Choose clarity over urgency',
      'Evaluate 5-10 year outlook',
    ],
    footer: 'Build a legacy, not just a transaction.',
  },
];
</script>

<style scoped>
.buyer-guidelines {
  padding: 80px 0;
  background: #fff;
}
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}
.section-header {
  margin-bottom: 60px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.tag {
  color: #b91c1c;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 0.8rem;
  display: block;
}
.section-header h2 {
  font-size: 2.5rem;
  font-weight: 800;
  margin-top: 10px;
  color: #111;
}

.guidelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 25px;
}

.guide-card {
  border-right: 1px solid #cfcece;
  border-bottom: 1px solid #cfcece;

  padding: 30px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  background: #fafafa;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.02);
}

.guide-card:hover {
  border-color: #b91c1c;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.icon-wrap {
  width: 22px;
  height: 22px;
  color: #b91c1c;
  flex-shrink: 0;
}
.card-header h3 {
  font-size: 1.2rem;
  margin: 0;
  font-weight: 800;
  color: #111;
  display: flex;
  align-items: center;
}

.step-num {
  color: #b91c1c;
  margin-right: 8px;
}

.sub-points {
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
  flex-grow: 1;
}

.sub-points li {
  color: #555;
  font-size: 0.95rem;
  padding-left: 15px;
  position: relative;
  margin-bottom: 8px;
  line-height: 1.4;
}

.sub-points li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  width: 4px;
  height: 1px;
  background: #b91c1c;
}

.guide-footer {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
  padding-top: 15px;
  border-top: 1px dashed #ddd;
}

@media (max-width: 768px) {
  .guidelines-grid {
    grid-template-columns: 1fr;
  }
  .section-header h2 {
    font-size: 2rem;
  }
}
</style>
